import { Input } from '@glu/form-components';
import { createUseStyles } from '@glu/theming';
import PropTypes from 'prop-types';
import React, { useRef, useEffect, useMemo } from 'react';
import locale from '@glu/locale';
import classnames from 'classnames';
import debounce from './utils/debounce';
import styles from './FilterInput.styles';
import { filterOptionsByString } from './utils/formatOptions';

const useStyles = createUseStyles(styles);

const FilterInput = ({
  applySelection,
  dark,
  filterBy,
  filterDebounce,
  filterHtmlValue,
  filterPlaceholder,
  filterString,
  isFocused,
  matchBy,
  multiselect,
  onFilter,
  options,
  setArrowDirection,
  setFilterHtmlValue,
  setFilterString,
  setFocusedIndex,
  setIsFilterFocused,
  setSelection
}) => {
  const classes = useStyles();
  const debouncer = useMemo(() => debounce(filter => filter(), 500), []);

  const inputRef = useRef();

  useEffect(() => {
    if (isFocused) {
      inputRef.current.querySelector('input').focus();
      setIsFilterFocused(false);
    }
  });

  const filteredOptions = filterOptionsByString(options, filterString, filterBy, matchBy);

  const handleChange = (event) => {
    const { value } = event.target;
    setFilterHtmlValue(value);

    if (filterDebounce) {
      filterDebounce(() => {
        onFilter(value);
        setFilterString(value);
      });
    } else {
      debouncer(() => {
        onFilter(value);
        setFilterString(value);
      });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      setArrowDirection('down');
      setFocusedIndex(1);
    }

    if (event.key === 'Enter') {
      if (filteredOptions.length === 0) {
        applySelection([]);
      } else if (multiselect) {
        setSelection(prev => prev.concat(filteredOptions[0]));
      } else {
        applySelection([filteredOptions.find(({ disabled }) => !disabled)]);
      }
    }

    if (event.key === 'Backspace') {
      setFilterString('');
    }
  };

  // Cannot add htmlId to this -- requires an update to form-components,
  // otherwise there will be duplicate IDs for a dropdown and input
  return (
    <div ref={inputRef} className={classes.root}>
      <Input
        autoComplete="off"
        className={classnames(classes.inputWrapper, { [classes.dark]: dark })}
        dark={dark}
        data-qa="filter-text-input"
        disableInputAPI
        labelText={filterPlaceholder || locale.get('filter')}
        name="filter"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={filterString ? '' : filterPlaceholder || locale.get('filter')}
        screenReaderLabel
        type="text"
        value={filterHtmlValue}
      />
      {filterBy === 'startsWith' && filterString && filteredOptions.length > 0
        && (
        <div className={classnames(classes.autocomplete, { [classes.dark]: dark })} data-qa="filter-autocomplete">
          <span className={classes.spacer} data-qa="input-spacer">{filterString}</span>
          <span className={classnames(classes.suggestion, { [classes.dark]: dark })} data-qa="input-suggestion">
            {filteredOptions[0].name.substr(filterString.length)}
          </span>
        </div>
        )}
    </div>
  );
};

FilterInput.propTypes = {
  applySelection: PropTypes.func.isRequired,
  dark: PropTypes.bool.isRequired,
  isFocused: PropTypes.bool.isRequired,
  onFilter: PropTypes.func.isRequired,
  setArrowDirection: PropTypes.func.isRequired,
  setFilterHtmlValue: PropTypes.func.isRequired,
  setFilterString: PropTypes.func.isRequired,
  setFocusedIndex: PropTypes.func.isRequired,
  setIsFilterFocused: PropTypes.func.isRequired,
  setSelection: PropTypes.func.isRequired,

  /** Setting to determine filter algorithm. */
  filterBy: PropTypes.string.isRequired,

  /** Custom function provided to overided to the default debounce the user's typing */
  filterDebounce: PropTypes.func,

  /** The html value of the filter input itself. */
  filterHtmlValue: PropTypes.string.isRequired,

  /** The user's typed input which options are filtered by  */
  filterString: PropTypes.string.isRequired,

  /** Placeholder text to display in filter input */
  filterPlaceholder: PropTypes.string,

  /** What properties to match on when filtering */
  matchBy: PropTypes.arrayOf(PropTypes.string).isRequired,

  /** UX setting to allow multiple selections */
  multiselect: PropTypes.bool.isRequired,

  /** All options for the current tree level */
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    disabled: PropTypes.bool,
    highlight: PropTypes.bool
  })).isRequired
};

FilterInput.defaultProps = {
  filterDebounce: undefined,
  filterPlaceholder: undefined
};

export default FilterInput;
